import { useState, useMemo } from "react";
import { Globe, Loader2, MapPin, TrendingUp, Heart, Zap } from "lucide-react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { usePrefetchOrFetch } from "../common/DataPrefetch";
import { Shimmer } from "../common/DashboardSkeleton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO 3166-1 country name to ISO alpha-3 mapping for react-simple-maps
const COUNTRY_TO_ISO3 = {
  "United States": "USA", "China": "CHN", "India": "IND", "United Kingdom": "GBR",
  "Germany": "DEU", "France": "FRA", "Canada": "CAN", "Japan": "JPN",
  "South Korea": "KOR", "Australia": "AUS", "Italy": "ITA", "Netherlands": "NLD",
  "Switzerland": "CHE", "Spain": "ESP", "Singapore": "SGP", "Brazil": "BRA",
  "Israel": "ISR", "Sweden": "SWE", "Belgium": "BEL", "Austria": "AUT",
  "Denmark": "DNK", "Finland": "FIN", "Norway": "NOR", "Poland": "POL",
  "Ireland": "IRL", "Portugal": "PRT", "Czech Republic": "CZE", "Turkey": "TUR",
  "Russia": "RUS", "Mexico": "MEX", "Chile": "CHL", "Argentina": "ARG",
  "Colombia": "COL", "South Africa": "ZAF", "Egypt": "EGY", "Nigeria": "NGA",
  "Kenya": "KEN", "Saudi Arabia": "SAU", "United Arab Emirates": "ARE",
  "Thailand": "THA", "Vietnam": "VNM", "Indonesia": "IDN", "Malaysia": "MYS",
  "Philippines": "PHL", "Pakistan": "PAK", "Bangladesh": "BGD", "Iran": "IRN",
  "Taiwan": "TWN", "Hong Kong": "HKG", "New Zealand": "NZL", "Romania": "ROU",
  "Greece": "GRC", "Hungary": "HUN", "Ukraine": "UKR", "Qatar": "QAT",
  "Lebanon": "LBN", "Jordan": "JOR", "Sri Lanka": "LKA", "Nepal": "NPL",
  "Peru": "PER", "Morocco": "MAR", "Tunisia": "TUN", "Ghana": "GHA",
  "Ethiopia": "ETH", "Tanzania": "TZA", "Uganda": "UGA",
};

// Name mapping from topojson property names to our country names
const GEO_NAME_MAP = {
  "United States of America": "United States",
  "South Korea": "South Korea",
  "Republic of Korea": "South Korea",
  "S. Korea": "South Korea",
  "Czech Rep.": "Czech Republic",
  "Czechia": "Czech Republic",
};

const getColor = (total, maxTotal, carePct) => {
  if (total === 0) return "#1e293b";
  const intensity = Math.min(total / Math.max(maxTotal * 0.3, 1), 1);
  // Blend from dark to cyan/green based on care percentage
  if (carePct >= 40) {
    // High care = emerald tones
    const r = Math.round(16 + (52 - 16) * intensity);
    const g = Math.round(30 + (211 - 30) * intensity);
    const b = Math.round(46 + (153 - 46) * intensity);
    return `rgb(${r},${g},${b})`;
  } else if (carePct >= 25) {
    // Medium care = cyan tones
    const r = Math.round(16 + (34 - 16) * intensity);
    const g = Math.round(30 + (211 - 30) * intensity);
    const b = Math.round(46 + (238 - 46) * intensity);
    return `rgb(${r},${g},${b})`;
  } else {
    // Low care = amber/warm tones
    const r = Math.round(16 + (251 - 16) * intensity);
    const g = Math.round(30 + (191 - 30) * intensity);
    const b = Math.round(46 + (36 - 46) * intensity);
    return `rgb(${r},${g},${b})`;
  }
};

export const GeoIntelligence = () => {
  const [data, loading] = usePrefetchOrFetch(
    "geoIntelligence",
    `${API}/research/geo-intelligence`,
    {
      validate: (d) => d && Array.isArray(d.countries) && typeof d.total_papers === "number",
      delay: 500,
    }
  );
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedView, setSelectedView] = useState("map");

  const countryMap = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.countries.forEach(c => {
      map[c.country] = c;
      const iso3 = COUNTRY_TO_ISO3[c.country];
      if (iso3) map[iso3] = c;
    });
    return map;
  }, [data]);

  const maxTotal = useMemo(() => {
    if (!data) return 1;
    return Math.max(...data.countries.map(c => c.total));
  }, [data]);

  const topCountries = useMemo(() => {
    if (!data) return [];
    return data.countries.slice(0, 15);
  }, [data]);

  const orientationSummary = useMemo(() => {
    if (!data) return [];
    const totals = { care: 0, utility: 0, creative: 0, extraction: 0 };
    data.countries.forEach(c => {
      totals.care += c.care;
      totals.utility += c.utility;
      totals.creative += c.creative;
      totals.extraction += c.extraction;
    });
    const total = Object.values(totals).reduce((a, b) => a + b, 0);
    return [
      { name: "Care", value: totals.care, pct: Math.round(totals.care / total * 100), fill: "#34d399" },
      { name: "Utility", value: totals.utility, pct: Math.round(totals.utility / total * 100), fill: "#60a5fa" },
      { name: "Creative", value: totals.creative, pct: Math.round(totals.creative / total * 100), fill: "#c084fc" },
      { name: "Extraction", value: totals.extraction, pct: Math.round(totals.extraction / total * 100), fill: "#f87171" },
    ];
  }, [data]);

  if (loading) return (
    <div className="space-y-5 animate-fade-in" data-testid="geo-intelligence-loading">
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <Shimmer className="h-3 w-48 mb-3" />
        <Shimmer className="h-[200px] w-full rounded-lg" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-2">
            <Shimmer className="h-2.5 w-16" />
            <Shimmer className="h-6 w-20" />
          </div>
        ))}
      </div>
      <p className="text-center text-[10px] text-slate-500 animate-pulse">Mapping global AI research...</p>
    </div>
  );

  if (!data) return null;

  const resolveCountryData = (geo) => {
    const iso3 = geo.properties?.ISO_A3 || geo.id;
    const name = geo.properties?.name || geo.properties?.NAME || "";
    const mappedName = GEO_NAME_MAP[name] || name;
    return countryMap[iso3] || countryMap[mappedName] || null;
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="geo-intelligence">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-3">
          <Globe className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[11px] text-emerald-400 tracking-wider uppercase">Geographic Intelligence</span>
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Global AI Research Atlas</h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto">
          <span className="text-emerald-400 font-semibold">{data.total_papers?.toLocaleString()}</span> research papers 
          across <span className="text-cyan-400 font-semibold">{data.total_countries}</span> countries. 
          Global care orientation: <span className="text-emerald-400 font-semibold">{data.global_care_pct}%</span>
        </p>
      </div>

      {/* View Toggle */}
      <div className="flex justify-center gap-1 mb-2">
        {[
          { id: "map", label: "World Map" },
          { id: "chart", label: "Top Countries" },
          { id: "insights", label: "Insights" },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => setSelectedView(v.id)}
            data-testid={`geo-view-${v.id}`}
            className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
              selectedView === v.id
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                : "text-slate-400 hover:text-slate-300 border border-transparent"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Map View */}
      {selectedView === "map" && (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 overflow-hidden">
          <div className="relative" style={{ maxHeight: "480px" }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 130, center: [10, 20] }}
              style={{ width: "100%", height: "auto" }}
              data-testid="geo-world-map"
            >
              <ZoomableGroup>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const cData = resolveCountryData(geo);
                      const total = cData?.total || 0;
                      const carePct = cData?.care_pct || 0;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          data-tooltip-id="geo-tooltip"
                          data-tooltip-content={cData ?
                            `${cData.country}: ${cData.total.toLocaleString()} papers | Care: ${cData.care_pct}% | Products: ${cData.products.toLocaleString()}` :
                            geo.properties?.name || "Unknown"
                          }
                          onMouseEnter={() => cData && setHoveredCountry(cData)}
                          onMouseLeave={() => setHoveredCountry(null)}
                          style={{
                            default: {
                              fill: getColor(total, maxTotal, carePct),
                              stroke: "#1e293b",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            hover: {
                              fill: total > 0 ? "#22d3ee" : "#334155",
                              stroke: "#0ea5e9",
                              strokeWidth: 1,
                              outline: "none",
                              cursor: total > 0 ? "pointer" : "default",
                            },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            <ReactTooltip id="geo-tooltip" className="!bg-slate-800 !text-xs !border !border-slate-600 !rounded-lg !px-3 !py-2" />

            {/* Legend */}
            <div className="absolute bottom-2 left-2 bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-lg p-2 text-[9px]">
              <p className="text-slate-400 mb-1 font-medium">Color = Care Orientation</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ background: "#fbbf24" }} />
                  <span className="text-slate-500">&lt;25%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ background: "#22d3ee" }} />
                  <span className="text-slate-500">25-40%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ background: "#34d399" }} />
                  <span className="text-slate-500">&gt;40%</span>
                </div>
              </div>
              <p className="text-slate-500 mt-1">Brightness = Paper volume</p>
            </div>
          </div>

          {/* Hover Info */}
          {hoveredCountry && (
            <div className="mt-2 p-3 bg-slate-900/60 border border-slate-700 rounded-lg flex items-center gap-6 text-xs">
              <div>
                <p className="text-white font-semibold">{hoveredCountry.country}</p>
                <p className="text-slate-500">{hoveredCountry.total.toLocaleString()} research papers</p>
              </div>
              <div className="flex gap-4">
                <div><span className="text-emerald-400 font-medium">{hoveredCountry.care_pct}%</span> <span className="text-slate-500">care</span></div>
                <div><span className="text-purple-400 font-medium">{hoveredCountry.products.toLocaleString()}</span> <span className="text-slate-500">products</span></div>
                <div><span className="text-cyan-400 font-medium">{hoveredCountry.avg_citations}</span> <span className="text-slate-500">avg citations</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chart View */}
      {selectedView === "chart" && (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Top 15 Countries by Research Output</h3>
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={topCountries} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis dataKey="country" type="category" tick={{ fill: "#e2e8f0", fontSize: 10 }} width={75} />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 text-xs shadow-xl">
                      <p className="text-white font-semibold">{d?.country}</p>
                      <p className="text-cyan-400">{d?.total?.toLocaleString()} papers</p>
                      <p className="text-emerald-400">Care: {d?.care_pct}%</p>
                      <p className="text-purple-400">Products: {d?.products?.toLocaleString()}</p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                {topCountries.map((c) => (
                  <Cell key={c.country} fill={getColor(c.total, maxTotal, c.care_pct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Insights View */}
      {selectedView === "insights" && (
        <div className="space-y-4">
          {/* Global Orientation Pie */}
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Global Research Orientation</h3>
            <div className="flex items-center gap-8 justify-center">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orientationSummary}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      strokeWidth={1}
                      stroke="#0f172a"
                    >
                      {orientationSummary.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {orientationSummary.map((o) => (
                  <div key={o.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: o.fill }} />
                    <span className="text-xs text-slate-300">{o.name}: <span className="font-medium" style={{ color: o.fill }}>{o.pct}%</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Most Care-Oriented */}
            <div className="bg-slate-800/40 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-4 h-4 text-emerald-400" />
                <h3 className="text-xs font-semibold text-white">Most Care-Oriented</h3>
              </div>
              <div className="space-y-1.5">
                {data.countries
                  .filter(c => c.total >= 20)
                  .sort((a, b) => b.care_pct - a.care_pct)
                  .slice(0, 5)
                  .map((c) => (
                    <div key={c.country} className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-300">{c.country}</span>
                      <span className="text-[11px] text-emerald-400 font-medium">{c.care_pct}%</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Highest Volume */}
            <div className="bg-slate-800/40 border border-cyan-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-cyan-400" />
                <h3 className="text-xs font-semibold text-white">Research Powerhouses</h3>
              </div>
              <div className="space-y-1.5">
                {data.countries.slice(0, 5).map((c) => (
                  <div key={c.country} className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-300">{c.country}</span>
                    <span className="text-[11px] text-cyan-400 font-medium">{c.total.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Most Products */}
            <div className="bg-slate-800/40 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-purple-400" />
                <h3 className="text-xs font-semibold text-white">Product Ecosystem</h3>
              </div>
              <div className="space-y-1.5">
                {data.countries
                  .sort((a, b) => b.products - a.products)
                  .slice(0, 5)
                  .map((c) => (
                    <div key={c.country} className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-300">{c.country}</span>
                      <span className="text-[11px] text-purple-400 font-medium">{c.products.toLocaleString()}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 text-[10px] text-slate-500 justify-center pt-2">
        <MapPin className="w-3 h-3" />
        <span>Geographic data from OpenAlex, Semantic Scholar, and institutional mapping across {data.total_countries} countries</span>
      </div>
    </div>
  );
};
